import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { generateClient } from 'aws-amplify/api';
import { Schema } from '../../../../../amplify/data/resource';
import TextField from '../../../../components/custom/TextField';
import Select from '../../../../components/custom/Select';
import Button from '../../../../components/custom/Button';
import DatePicker from '../../../../components/custom/DatePicker';
import { Dayjs } from 'dayjs';

const apiClient = generateClient<Schema>();

interface AddCouponDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

interface CouponFormData {
  code: string;
  description: string;
  discountType: 'PERCENT' | 'AMOUNT';
  discountValue: string;
  startDate: Dayjs | null;
  expirationDate: Dayjs | null;
  usageLimit: string;
}

const AddCouponDialog: React.FC<AddCouponDialogProps> = ({ open, onClose, onSubmit }) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CouponFormData>({
    defaultValues: {
      code: '',
      description: '',
      discountType: 'PERCENT',
      discountValue: '',
      startDate: null,
      expirationDate: null,
      usageLimit: '',
    },
  });

  const discountTypeOptions = [
    { label: 'אחוז', value: 'PERCENT' },
    { label: 'סכום קבוע', value: 'AMOUNT' },
  ];

  const handleFormSubmit = async (data: CouponFormData) => {
    try {
      const submitData = {
        code: data.code,
        description: data.description,
        discountType: data.discountType,
        discountValue: parseFloat(data.discountValue),
        startDate: data.startDate ? data.startDate.toISOString() : new Date().toISOString(),
        expirationDate: data.expirationDate ? data.expirationDate.toISOString() : new Date().toISOString(),
        usageLimit: data.usageLimit ? parseInt(data.usageLimit) : null,
        totalUses: 0,
        status: 'ACTIVE' as const,
      };

      // Создаем купон в базе данных
      const result = await apiClient.models.Coupon.create(submitData);
      
      if (result.data) {
        console.log('Coupon created successfully:', result.data);
        enqueueSnackbar('קופון נוצר בהצלחה!', { variant: 'success' });
        onSubmit(result.data); // Передаем созданный объект родителю
        reset();
        onClose(); // Закрываем диалог только при успехе
      } else {
        throw new Error('Failed to create coupon');
      }
    } catch (error) {
      console.error('Error creating coupon:', error);
      enqueueSnackbar('שגיאה ביצירת הקופון. אנא נסה שוב.', { variant: 'error' });
      // При ошибке диалог остается открытым и форма не сбрасывается
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle sx={{ direction: 'rtl' }}>
        <Typography
          fontSize="24px"
          fontWeight="700"
          fontFamily="AlmoniDL"
          textAlign="center"
        >
          הוסף קופון חדש
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ direction: 'rtl', padding: '24px' }}>
        <Stack spacing={3}>
          <Controller
            name="code"
            control={control}
            rules={{ required: 'קוד קופון הוא שדה חובה' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="קוד קופון"
                error={!!errors.code}
                helperText={errors.code?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="תיאור (אופציונלי)"
                fullWidth
              />
            )}
          />

          <Controller
            name="discountType"
            control={control}
            rules={{ required: 'סוג הנחה הוא שדה חובה' }}
            render={({ field }) => (
              <Select
                {...field}
                label="סוג הנחה"
                options={discountTypeOptions}
                error={!!errors.discountType}
                helperText={errors.discountType?.message}
                id="discountType"
              />
            )}
          />

          <Controller
            name="discountValue"
            control={control}
            rules={{ 
              required: 'ערך הנחה הוא שדה חובה',
              pattern: {
                value: /^\d+(\.\d+)?$/,
                message: 'יש להזין מספר תקין'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="ערך הנחה"
                error={!!errors.discountValue}
                helperText={errors.discountValue?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="startDate"
            control={control}
            rules={{ required: 'תאריך התחלה הוא שדה חובה' }}
            render={({ field }) => (
              <DatePicker
                label="תאריך התחלה"
                value={field.value}
                onChange={field.onChange}
                error={!!errors.startDate}
                helperText={errors.startDate?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="expirationDate"
            control={control}
            rules={{ required: 'תאריך סיום הוא שדה חובה' }}
            render={({ field }) => (
              <DatePicker
                label="תאריך סיום"
                value={field.value}
                onChange={field.onChange}
                error={!!errors.expirationDate}
                helperText={errors.expirationDate?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="usageLimit"
            control={control}
            rules={{
              pattern: {
                value: /^\d+$/,
                message: 'יש להזין מספר שלם'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="מגבלת שימוש (אופציונלי)"
                error={!!errors.usageLimit}
                helperText={errors.usageLimit?.message}
                fullWidth
                placeholder="השאר ריק ללא מגבלה"
              />
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ direction: 'rtl', padding: '16px 24px', gap: '12px' }}>
        <Button
          onClick={handleClose}
          customVariant="light"
        >
          ביטול
        </Button>
        <Button
          onClick={handleSubmit(handleFormSubmit)}
          customVariant="default"
          loading={isSubmitting}
        >
          שמור קופון
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCouponDialog; 